import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { api, type Mailbox, type MessageDetail, type MessageSummary } from './lib/api'

type Counts = Record<
  string,
  {
    total: number
    unread: number
    sizeBytes: number | null
    sizeComputedAt: number | null
  } | null
>

const SIZE_STALE_SECONDS = 24 * 60 * 60

export type MailboxManagerHandle = {
  refreshAll: (onProgress?: (done: number, total: number) => void) => Promise<void>
  refreshAllSizes: (onProgress?: (done: number, total: number) => void) => Promise<void>
  deleteAll: (onProgress?: (done: number, total: number) => void) => Promise<void>
}

export const MailboxManager = forwardRef<MailboxManagerHandle, {}>(function MailboxManager(_props, ref) {
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([])
  const [messagesByMailbox, setMessagesByMailbox] = useState<Record<string, MessageSummary[]>>({})
  const [loadingByMailbox, setLoadingByMailbox] = useState<Record<string, boolean>>({})
  const [counts, setCounts] = useState<Counts>({})
  const [openContext, setOpenContext] = useState<{ smtp: string; messageId: string } | null>(null)
  const [openMessage, setOpenMessage] = useState<MessageDetail | null>(null)
  const [error, setError] = useState<string | null>(null)

  const tileRefs = useRef<Record<string, HTMLDivElement | null>>({})

  useEffect(() => {
    api.listMailboxes().then(rows => {
      setMailboxes(rows)
    })
  }, [])

  useEffect(() => {
    if (mailboxes.length === 0) return
    void refreshCounts()
    const id = window.setInterval(() => void refreshCounts(), 60_000)
    return () => window.clearInterval(id)
  }, [mailboxes.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load messages for each mailbox once it appears in the list.
  useEffect(() => {
    for (const mb of mailboxes) {
      if (messagesByMailbox[mb.smtp] !== undefined) continue
      void loadMessages(mb.smtp)
    }
  }, [mailboxes]) // eslint-disable-line react-hooks/exhaustive-deps

  async function loadMessages(smtp: string) {
    setLoadingByMailbox(prev => ({ ...prev, [smtp]: true }))
    try {
      const msgs = await api.listMessages(smtp, 15)
      setMessagesByMailbox(prev => ({ ...prev, [smtp]: msgs }))
    } catch (e) {
      console.error('list messages failed', smtp, e)
      setMessagesByMailbox(prev => ({ ...prev, [smtp]: [] }))
    } finally {
      setLoadingByMailbox(prev => ({ ...prev, [smtp]: false }))
    }
  }

  const prevCountsRef = useRef<Counts>({})
  async function refreshCounts() {
    try {
      const next = await api.mailboxCounts()
      const prev = prevCountsRef.current
      // Refetch messages for any mailbox whose counts changed.
      for (const [smtp, c] of Object.entries(next)) {
        const p = prev[smtp]
        if (!c) continue
        if (!p || p.total !== c.total || p.unread !== c.unread) {
          void loadMessages(smtp)
        }
      }
      prevCountsRef.current = next
      setCounts(next)
    } catch {
      /* best-effort */
    }
  }

  async function onAdd(smtp: string, displayName: string) {
    setError(null)
    try {
      const mb = await api.addMailbox(smtp, displayName || undefined)
      setMailboxes(prev => [...prev, mb].sort((a, b) => a.smtp.localeCompare(b.smtp)))
    } catch (e) {
      setError(prettyError(e))
      throw e
    }
  }

  async function onRemove(id: string) {
    if (!confirm('Remove this mailbox from your list? (does not affect Outlook)')) return
    const mb = mailboxes.find(m => m.id === id)
    await api.deleteMailbox(id)
    setMailboxes(prev => prev.filter(m => m.id !== id))
    if (mb) {
      setMessagesByMailbox(prev => {
        const next = { ...prev }
        delete next[mb.smtp]
        return next
      })
    }
  }

  async function onOpenMessage(smtp: string, id: string) {
    setOpenContext({ smtp, messageId: id })
    setOpenMessage(null)
    try {
      const msg = await api.readMessage(smtp, id)
      setOpenMessage(msg)
      if (!msg.isRead) {
        await api.setMessageRead(smtp, id, true)
        setMessagesByMailbox(prev => ({
          ...prev,
          [smtp]: (prev[smtp] ?? []).map(m => (m.id === id ? { ...m, isRead: true } : m))
        }))
        void refreshCounts()
      }
    } catch (e) {
      setError(prettyError(e))
      setOpenContext(null)
    }
  }

  function closePreview() {
    setOpenContext(null)
    setOpenMessage(null)
  }

  async function onDeleteMessage(smtp: string, id: string) {
    if (!confirm('Move this message to Deleted Items?')) return
    await api.deleteMessage(smtp, id)
    setMessagesByMailbox(prev => ({
      ...prev,
      [smtp]: (prev[smtp] ?? []).filter(m => m.id !== id)
    }))
    if (openContext?.messageId === id) closePreview()
    void refreshCounts()
  }

  function scrollToTile(smtp: string) {
    const el = tileRefs.current[smtp]
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Sequentially refresh the cached size for every mailbox. Each call sums
  // the Graph `/messages?$select=size` pages server-side and writes the total
  // into D1. We go one at a time because a single mailbox can consume most of
  // a Worker invocation's subrequest budget.
  async function refreshAllSizes(onProgress?: (done: number, total: number) => void) {
    for (let i = 0; i < mailboxes.length; i++) {
      onProgress?.(i, mailboxes.length)
      try {
        const r = await api.refreshMailboxSize(mailboxes[i].smtp)
        if (!r.timedOut && r.sizeBytes != null && r.sizeComputedAt != null) {
          const smtp = mailboxes[i].smtp
          setCounts(prev => {
            const cur = prev[smtp]
            if (!cur) return prev
            return {
              ...prev,
              [smtp]: { ...cur, sizeBytes: r.sizeBytes!, sizeComputedAt: r.sizeComputedAt! }
            }
          })
        }
      } catch (e) {
        console.error('refresh size failed', mailboxes[i].smtp, e)
      }
    }
    onProgress?.(mailboxes.length, mailboxes.length)
  }

  // Auto-refresh stale sizes (>24h old or never computed) once per page load.
  const sizeAutoRan = useRef(false)
  useEffect(() => {
    if (sizeAutoRan.current) return
    if (mailboxes.length === 0) return
    const nowSec = Math.floor(Date.now() / 1000)
    const anyStale = mailboxes.some(mb => {
      const c = counts[mb.smtp]
      if (!c) return false // counts haven't loaded yet; skip — next effect run will re-check
      return c.sizeComputedAt == null || nowSec - c.sizeComputedAt > SIZE_STALE_SECONDS
    })
    const haveAllCounts = mailboxes.every(mb => counts[mb.smtp] !== undefined)
    if (haveAllCounts && anyStale) {
      sizeAutoRan.current = true
      void refreshAllSizes()
    }
  }, [mailboxes, counts]) // eslint-disable-line react-hooks/exhaustive-deps

  // Expose imperative actions to App's top-bar buttons.
  useImperativeHandle(
    ref,
    () => ({
      async refreshAll(onProgress) {
        await refreshCounts()
        for (let i = 0; i < mailboxes.length; i++) {
          onProgress?.(i, mailboxes.length)
          await loadMessages(mailboxes[i].smtp)
        }
        onProgress?.(mailboxes.length, mailboxes.length)
      },
      refreshAllSizes,
      async deleteAll(onProgress) {
        if (mailboxes.length === 0) return
        for (let i = 0; i < mailboxes.length; i++) {
          onProgress?.(i, mailboxes.length)
          await purgeOne(mailboxes[i].smtp, { refresh: false })
        }
        onProgress?.(mailboxes.length, mailboxes.length)
        // One reconcile pass at the end, delayed: Graph counts/lists are
        // eventually consistent after a mass delete — refetching immediately
        // hands back the just-deleted messages and repopulates the tiles.
        window.setTimeout(() => void refreshCounts(), 5_000)
      }
    }),
    [mailboxes] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const [purgingSmtp, setPurgingSmtp] = useState<string | null>(null)

  // After a purge, zero the tile locally instead of refetching right away:
  // Graph is eventually consistent after mass deletes and would hand the
  // just-deleted messages straight back. Zeroing prevCountsRef too keeps the
  // 60s poller from seeing a stale "change" and reloading the old list.
  function zeroMailboxDisplay(smtp: string) {
    const zero = (cur: Counts[string]) =>
      cur
        ? { ...cur, total: 0, unread: 0 }
        : { total: 0, unread: 0, sizeBytes: null, sizeComputedAt: null }
    setMessagesByMailbox(prev => ({ ...prev, [smtp]: [] }))
    setCounts(prev => ({ ...prev, [smtp]: zero(prev[smtp] ?? null) }))
    prevCountsRef.current = {
      ...prevCountsRef.current,
      [smtp]: zero(prevCountsRef.current[smtp] ?? null)
    }
  }

  // Repeatedly call purge until the server reports it's done (handles big mailboxes).
  async function purgeOne(smtp: string, opts?: { refresh?: boolean }): Promise<void> {
    setPurgingSmtp(smtp)
    try {
      let totalPurged = 0
      let safety = 20 // up to 20 rounds (~40k items) per click — bail otherwise
      while (safety-- > 0) {
        const r = await api.purgeMailbox(smtp)
        totalPurged += r.total
        if (!r.timedOut) break
      }
      zeroMailboxDisplay(smtp)
      if (opts?.refresh !== false) {
        window.setTimeout(() => void refreshCounts(), 5_000)
      }
      console.log('[purge] done', smtp, 'items purged:', totalPurged)
    } catch (e) {
      setError(prettyError(e))
    } finally {
      setPurgingSmtp(null)
    }
  }

  function deleteMailboxContents(smtp: string) {
    void purgeOne(smtp)
  }

  // ---- Discover ----
  const [discovering, setDiscovering] = useState(false)
  const [discoverResult, setDiscoverResult] = useState<{
    scanned: number
    truncated: boolean
    matches: Array<{ mail: string; displayName: string }>
  } | null>(null)
  const [adding, setAdding] = useState<string | null>(null)
  const [addedSmtps, setAddedSmtps] = useState<Set<string>>(new Set())
  const [addingAll, setAddingAll] = useState(false)

  async function onDiscover() {
    setError(null)
    setDiscovering(true)
    setDiscoverResult(null)
    setAddedSmtps(new Set())
    try {
      setDiscoverResult(await api.discoverMailboxes())
    } catch (e) {
      setError(prettyError(e))
    } finally {
      setDiscovering(false)
    }
  }

  async function onAddDiscovered(smtp: string, displayName: string) {
    if (addedSmtps.has(smtp.toLowerCase())) return
    setAdding(smtp)
    try {
      const mb = await api.addMailbox(smtp, displayName)
      setMailboxes(prev => [...prev, mb].sort((a, b) => a.smtp.localeCompare(b.smtp)))
      setAddedSmtps(prev => new Set(prev).add(smtp.toLowerCase()))
    } catch (e) {
      setError(prettyError(e))
    } finally {
      setAdding(null)
    }
  }

  async function onAddAll() {
    if (!discoverResult) return
    setAddingAll(true)
    const localAdded = new Set(addedSmtps)
    for (const m of discoverResult.matches) {
      if (localAdded.has(m.mail.toLowerCase())) continue
      setAdding(m.mail)
      try {
        const mb = await api.addMailbox(m.mail, m.displayName)
        setMailboxes(prev => [...prev, mb].sort((a, b) => a.smtp.localeCompare(b.smtp)))
        localAdded.add(m.mail.toLowerCase())
        setAddedSmtps(new Set(localAdded))
      } catch (e) {
        setError(prettyError(e))
      }
    }
    setAdding(null)
    setAddingAll(false)
  }

  return (
    <div className="flex h-full min-h-0 gap-4">
      <aside
        className="flex shrink-0 flex-col rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900/40 dark:shadow-none"
        style={{ width: '10%', minWidth: 160 }}
      >
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
          Mailboxes ({mailboxes.length})
        </div>
        <ul className="flex-1 min-h-0 space-y-1 overflow-auto">
          {mailboxes.map(mb => {
            const c = counts[mb.smtp]
            const unread = c?.unread ?? 0
            return (
              <li key={mb.id}>
                <button
                  onClick={() => scrollToTile(mb.smtp)}
                  className="flex w-full items-start justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-800/60"
                >
                  <span className={`min-w-0 flex-1 truncate ${unread > 0 ? 'font-semibold' : ''}`}>
                    <div className="truncate">{mb.display_name ?? mb.smtp}</div>
                  </span>
                  <span
                    onClick={e => {
                      e.stopPropagation()
                      void onRemove(mb.id)
                    }}
                    className="shrink-0 cursor-pointer text-xs text-slate-500 hover:text-rose-500 dark:hover:text-rose-400"
                    title="Remove"
                  >
                    ✕
                  </span>
                </button>
              </li>
            )
          })}
          {mailboxes.length === 0 && (
            <li className="px-2 py-2 text-xs text-slate-500">No mailboxes yet.</li>
          )}
        </ul>
        <AddMailboxForm onAdd={onAdd} />
        <button
          onClick={onDiscover}
          disabled={discovering}
          className="mt-2 w-full rounded-md border border-slate-300 px-2 py-2 text-xs text-slate-700 hover:border-slate-500 hover:text-slate-900 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:border-slate-500 dark:hover:text-slate-100"
        >
          {discovering ? (
            <span className="flex items-center justify-center gap-2">
              <Spinner /> Scanning tenant…
            </span>
          ) : (
            'Discover mailboxes'
          )}
        </button>
      </aside>

      <main className="flex-1 min-w-0 min-h-0 overflow-auto pr-2.5">
        {mailboxes.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-slate-500">
            Add a mailbox or click <span className="mx-1 text-slate-700 dark:text-slate-300">Discover mailboxes</span> to get started.
          </div>
        ) : (
          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}
          >
            {mailboxes.map(mb => (
              <MailboxTile
                key={mb.id}
                ref={el => {
                  tileRefs.current[mb.smtp] = el
                }}
                mailbox={mb}
                messages={messagesByMailbox[mb.smtp]}
                loading={!!loadingByMailbox[mb.smtp]}
                counts={counts[mb.smtp]}
                purging={purgingSmtp === mb.smtp}
                onRefresh={() => void loadMessages(mb.smtp)}
                onDeleteMailbox={() => deleteMailboxContents(mb.smtp)}
                onOpenMessage={id => void onOpenMessage(mb.smtp, id)}
                openMessageId={openContext?.smtp === mb.smtp ? openContext.messageId : null}
              />
            ))}
          </div>
        )}
      </main>

      {openContext && (
        <MessagePreview
          context={openContext}
          message={openMessage}
          onClose={closePreview}
          onDelete={() => void onDeleteMessage(openContext.smtp, openContext.messageId)}
        />
      )}

      {error && (
        <div className="absolute bottom-4 left-4 right-4 rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-700 dark:bg-rose-900/20 dark:text-rose-300">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-rose-500 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-200"
          >
            ✕
          </button>
        </div>
      )}

      {discoverResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6 dark:bg-black/70">
          <div className="w-full max-w-lg overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
            <header className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-800">
              <div className="min-w-0">
                <div className="text-sm font-semibold">Discovered mailboxes</div>
                <div className="text-xs text-slate-500">
                  Scanned {discoverResult.scanned} user{discoverResult.scanned === 1 ? '' : 's'}
                  {discoverResult.truncated && ' (truncated at 500)'} · found{' '}
                  {discoverResult.matches.length} · added {addedSmtps.size}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {discoverResult.matches.length > 0 && (
                  <button
                    onClick={onAddAll}
                    disabled={addingAll || addedSmtps.size === discoverResult.matches.length}
                    className="rounded-md bg-sky-600 px-2 py-1 text-xs font-medium text-white hover:bg-sky-500 disabled:opacity-50"
                  >
                    {addingAll ? (
                      <span className="flex items-center gap-1">
                        <Spinner /> Adding…
                      </span>
                    ) : (
                      `Add all (${discoverResult.matches.length - addedSmtps.size})`
                    )}
                  </button>
                )}
                <button
                  onClick={() => setDiscoverResult(null)}
                  className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:border-slate-500 dark:border-slate-700 dark:text-slate-300 dark:hover:border-slate-500"
                >
                  Close
                </button>
              </div>
            </header>
            <div className="max-h-96 overflow-auto">
              {discoverResult.matches.length === 0 ? (
                <div className="p-4 text-sm text-slate-600 dark:text-slate-400">
                  No mailboxes found that you have delegate access to (or all are already added).
                </div>
              ) : (
                <ul className="divide-y divide-slate-200 dark:divide-slate-800">
                  {discoverResult.matches.map(m => {
                    const isAdded = addedSmtps.has(m.mail.toLowerCase())
                    const isAddingThis = adding === m.mail
                    return (
                      <li key={m.mail} className="flex items-center justify-between gap-3 px-4 py-2">
                        <div className="min-w-0">
                          <div className={`truncate text-sm ${isAdded ? 'text-slate-500' : ''}`}>
                            {m.displayName}
                          </div>
                          <div className="truncate text-xs text-slate-500">{m.mail}</div>
                        </div>
                        {isAdded ? (
                          <span className="flex shrink-0 items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                            <CheckIcon /> Added
                          </span>
                        ) : (
                          <button
                            onClick={() => onAddDiscovered(m.mail, m.displayName)}
                            disabled={isAddingThis || addingAll}
                            className="shrink-0 rounded-md bg-sky-600 px-2 py-1 text-xs font-medium text-white hover:bg-sky-500 disabled:opacity-50"
                          >
                            {isAddingThis ? 'Adding…' : 'Add'}
                          </button>
                        )}
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
})

type MailboxTileProps = {
  mailbox: Mailbox
  messages: MessageSummary[] | undefined
  loading: boolean
  counts: { total: number; unread: number; sizeBytes: number | null } | null | undefined
  purging: boolean
  openMessageId: string | null
  onOpenMessage: (id: string) => void
  onRefresh: () => void
  onDeleteMailbox: () => void
}

const MailboxTile = forwardRef<HTMLDivElement, MailboxTileProps>(function MailboxTile(
  {
    mailbox,
    messages,
    loading,
    counts,
    purging,
    openMessageId,
    onOpenMessage,
    onRefresh,
    onDeleteMailbox
  },
  ref
) {
  const unread = counts?.unread ?? 0
  const total = counts?.total
  const size = formatSize(counts?.sizeBytes)
  return (
    <div
      ref={ref}
      className="flex h-80 min-h-0 flex-col rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900/40 dark:shadow-none"
    >
      <header className="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2 dark:border-slate-800">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="truncate text-sm font-semibold">
              {mailbox.display_name ?? mailbox.smtp}
            </div>
            {size && (
              <span
                className="shrink-0 text-[10px] tabular-nums text-slate-500"
                title="Total mailbox size"
              >
                {size}
              </span>
            )}
            {counts === undefined ? (
              <span className="shrink-0 text-[10px] text-slate-400 dark:text-slate-600">…</span>
            ) : counts === null ? (
              <span
                className="shrink-0 text-[10px] text-slate-400 dark:text-slate-600"
                title="Couldn't fetch counts"
              >
                —
              </span>
            ) : (
              <span
                className="flex shrink-0 items-center gap-1 text-[10px] tabular-nums text-slate-500"
                title={`${unread} unread / ${total} total`}
              >
                {unread > 0 && (
                  <span className="rounded bg-sky-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                    {unread}
                  </span>
                )}
                <span>{total ?? 0}</span>
              </span>
            )}
          </div>
          {mailbox.display_name && (
            <div className="truncate text-[10px] text-slate-500">{mailbox.smtp}</div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            onClick={onRefresh}
            className="text-xs text-slate-500 hover:text-slate-900 dark:hover:text-slate-200"
            title="Refresh"
          >
            ↻
          </button>
          <button
            onClick={onDeleteMailbox}
            disabled={purging}
            className="rounded border border-slate-300 px-1.5 py-0.5 text-[10px] text-slate-600 hover:border-rose-500 hover:text-rose-500 disabled:opacity-60 dark:border-slate-700 dark:text-slate-400 dark:hover:border-rose-500 dark:hover:text-rose-400"
            title="Purge Inbox + Sent + Deleted (bypasses trash, lands in Recoverable Items)"
          >
            {purging ? (
              <span className="flex items-center gap-1">
                <Spinner /> Purging…
              </span>
            ) : (
              'Delete Mailbox'
            )}
          </button>
        </div>
      </header>
      <div className="flex-1 min-h-0 overflow-auto">
        {loading && messages === undefined && (
          <div className="p-3 text-xs text-slate-500">Loading…</div>
        )}
        {messages && messages.length === 0 && (
          <div className="p-3 text-xs text-slate-500">No messages.</div>
        )}
        <ul className="divide-y divide-slate-200 dark:divide-slate-800">
          {(messages ?? []).map(m => (
            <li key={m.id}>
              <button
                onClick={() => onOpenMessage(m.id)}
                className={`block w-full px-3 py-2 text-left text-xs hover:bg-slate-100 dark:hover:bg-slate-800/40 ${
                  openMessageId === m.id ? 'bg-slate-100 dark:bg-slate-800/60' : ''
                }`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className={`truncate ${m.isRead ? 'text-slate-700 dark:text-slate-300' : 'font-semibold'}`}>
                    {m.fromName ?? m.fromAddress ?? '(unknown)'}
                  </span>
                  <span className="shrink-0 text-[10px] text-slate-500">
                    {formatDate(m.receivedAt)}
                  </span>
                </div>
                <div
                  className={`truncate ${
                    m.isRead ? 'text-slate-500 dark:text-slate-400' : 'text-slate-900 dark:text-slate-100'
                  }`}
                >
                  {m.subject || '(no subject)'}
                </div>
                <div className="truncate text-[11px] text-slate-500">{m.preview}</div>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
})

function MessagePreview({
  context,
  message,
  onClose,
  onDelete
}: {
  context: { smtp: string; messageId: string }
  message: MessageDetail | null
  onClose: () => void
  onDelete: () => void
}) {
  // close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-6 dark:bg-black/70"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900"
        onClick={e => e.stopPropagation()}
      >
        {!message ? (
          <div className="flex items-center justify-center p-12 text-sm text-slate-600 dark:text-slate-400">
            Loading message…
          </div>
        ) : (
          <>
            <header className="flex items-start justify-between gap-3 border-b border-slate-200 p-4 dark:border-slate-800">
              <div className="min-w-0 flex-1">
                <div className="truncate text-base font-semibold">{message.subject}</div>
                <div className="mt-1 truncate text-xs text-slate-600 dark:text-slate-400">
                  {message.fromName ?? ''}{' '}
                  <span className="text-slate-500">&lt;{message.fromAddress ?? ''}&gt;</span>
                </div>
                <div className="truncate text-xs text-slate-500">
                  to {message.to.join(', ')} · in {context.smtp}
                </div>
                <div className="text-[11px] text-slate-500">{formatDate(message.receivedAt)}</div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  onClick={onDelete}
                  className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:border-rose-500 hover:text-rose-500 dark:border-slate-700 dark:text-slate-300 dark:hover:border-rose-500 dark:hover:text-rose-400"
                >
                  Delete
                </button>
                <button
                  onClick={onClose}
                  className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:border-slate-500 dark:border-slate-700 dark:text-slate-300 dark:hover:border-slate-500"
                >
                  Close
                </button>
              </div>
            </header>
            <pre className="flex-1 min-h-0 overflow-auto whitespace-pre-wrap break-words p-4 font-sans text-sm text-slate-800 dark:text-slate-200">
              {message.bodyText}
            </pre>
          </>
        )}
      </div>
    </div>
  )
}

type Suggestion = { id: string; displayName: string; mail: string; upn: string }

function AddMailboxForm({ onAdd }: { onAdd: (smtp: string, name: string) => Promise<void> }) {
  const [open, setOpen] = useState(false)
  const [smtp, setSmtp] = useState('')
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [searching, setSearching] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)

  useEffect(() => {
    const q = smtp.trim()
    if (q.length < 2) {
      setSuggestions([])
      return
    }
    let cancelled = false
    setSearching(true)
    const handle = window.setTimeout(async () => {
      try {
        const users = await api.searchUsers(q)
        if (!cancelled) setSuggestions(users)
      } catch {
        if (!cancelled) setSuggestions([])
      } finally {
        if (!cancelled) setSearching(false)
      }
    }, 250)
    return () => {
      cancelled = true
      window.clearTimeout(handle)
    }
  }, [smtp])

  function pick(s: Suggestion) {
    setSmtp(s.mail)
    setName(s.displayName)
    setShowSuggestions(false)
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      await onAdd(smtp.trim().toLowerCase(), name.trim())
      setSmtp('')
      setName('')
      setSuggestions([])
      setOpen(false)
    } catch {
      /* parent surfaces error */
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="mt-3 w-full rounded-md border border-dashed border-slate-300 px-2 py-2 text-xs text-slate-600 hover:border-slate-500 hover:text-slate-900 dark:border-slate-700 dark:text-slate-400 dark:hover:border-slate-500 dark:hover:text-slate-200"
      >
        + Add mailbox
      </button>
    )
  }

  return (
    <form
      onSubmit={submit}
      className="mt-3 space-y-2 rounded-md border border-slate-300 p-2 dark:border-slate-700"
    >
      <div className="relative">
        <input
          autoFocus
          type="text"
          required
          value={smtp}
          onChange={e => {
            setSmtp(e.target.value)
            setShowSuggestions(true)
          }}
          onFocus={() => setShowSuggestions(true)}
          placeholder="Search or type SMTP…"
          className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-950"
          autoComplete="off"
        />
        {showSuggestions && (suggestions.length > 0 || searching) && (
          <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-auto rounded border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900">
            {searching && <div className="px-2 py-1 text-[11px] text-slate-500">Searching…</div>}
            {suggestions.map(s => (
              <button
                type="button"
                key={s.id}
                onMouseDown={e => {
                  e.preventDefault()
                  pick(s)
                }}
                className="block w-full px-2 py-1 text-left text-xs hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <div className="truncate">{s.displayName}</div>
                <div className="truncate text-[10px] text-slate-500">{s.mail}</div>
              </button>
            ))}
          </div>
        )}
      </div>
      <input
        type="text"
        value={name}
        onChange={e => setName(e.target.value)}
        placeholder="Display name (optional)"
        className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-950"
      />
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={busy || !smtp}
          className="flex-1 rounded bg-sky-600 px-2 py-1 text-xs text-white hover:bg-sky-500 disabled:opacity-50"
        >
          {busy ? 'Verifying…' : 'Add'}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:text-slate-900 dark:border-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}

function Spinner() {
  return (
    <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-slate-500 border-t-transparent" />
  )
}

function CheckIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className="h-3 w-3"
    >
      <path
        fillRule="evenodd"
        d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.42 0l-3.5-3.5a1 1 0 011.42-1.42L8.5 12.08l6.79-6.79a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function formatSize(bytes: number | null | undefined): string | null {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return null
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let value = bytes / 1024
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit++
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

function prettyError(e: unknown): string {
  const code = e instanceof Error ? e.message : 'unknown'
  const table: Record<string, string> = {
    invalid_smtp: "That doesn't look like a valid email address.",
    no_delegate_access: 'Your Microsoft account does not have delegate access to that mailbox.',
    mailbox_not_found: 'No mailbox found at that address.',
    already_added: 'That mailbox is already in your list.',
    microsoft_not_linked: 'Link your Microsoft account first.',
    rate_limited: 'Too many requests. Slow down.',
    forbidden: "You haven't added that mailbox.",
    probe_failed: 'Microsoft Graph rejected the probe. Try again.'
  }
  return table[code] ?? `Error: ${code}`
}

